import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { FormControlNames } from '../../../../référentiel-métier-parcours/models/form-control-names.model';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { TechniqueAnimation } from '../../model/technique-animation.model';
import { CreateRequestTechniqueAnimation } from '../../request/create-request';
import { TechniqueAnimationStore } from '../../store/technique-animation.store';

@Component({
  selector: 'app-form-technique-animation',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    ButtonAddNewComponent,
    HlmInputImports,
    HlmCardImports,
    ExpansionPanel,
  ],
  templateUrl: './form-technique-animation.component.html',
})
export class FormTechniqueAnimationComponent implements OnInit {
  readonly store = inject(TechniqueAnimationStore);
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly techniqueAnimation = input<TechniqueAnimation>();

  form = this.#fb.group({
    [FormControlNames.TECHNIQUES_ANIMATION]: this.#fb.array([]),
  });

  readonly techniqueActions: ExpansionPanelMenuAction<number>[] = [
    {
      icon: 'lucideTrash',
      invisible: (idx) => idx === 0,
      label: 'Supprimer',
      class: 'invisible group-hover/item:visible',
      action: (idx) => this.techniquesAnimation.removeAt(idx!),
    },
  ];

  constructor() {
    effect(() => {
      const techniqueAnimation = this.techniqueAnimation();

      if (techniqueAnimation) {
        this.techniquesAnimation.clear();
        this.ajouterTechniqueAnimation(techniqueAnimation);
      }
    });
  }

  ngOnInit(): void {
    this.ajouterTechniqueAnimation();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterTechniqueAnimation(technique?: TechniqueAnimation) {
    const techniqueAnimationForm = this.#fb.group({
      id: [technique?.id],
      libelle: [technique?.libelle, Validators.required],
      lien: [technique?.lien],
    });
    this.techniquesAnimation.push(techniqueAnimationForm);
  }

  onSubmit() {
    const toastrId = uuidv7();

    if (this.form.invalid) return;

    const techniqueAnimation = this.techniqueAnimation();

    if (techniqueAnimation) {
      const techniqueModifiée = (this.techniquesAnimation.value as TechniqueAnimation[])[0];
      this.store.modifier({
        techniqueAnimation: techniqueModifiée,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: `Modification de la technique d'animation ${techniqueModifiée.libelle} réussie`,
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.store.charger();
        },
      });
    } else {
      const data: CreateRequestTechniqueAnimation = {
        techniquesAnimation: (this.techniquesAnimation.value as TechniqueAnimation[]).map((technique) => ({
          libelle: technique.libelle,
          lien: technique.lien,
        })),
      };

      this.store.ajouter({
        data,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: "Création des techniques d'animation réussie",
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.store.charger();
        },
      });
    }
  }

  get techniquesAnimation() {
    return this.form.get(FormControlNames.TECHNIQUES_ANIMATION) as FormArray;
  }
}
